import * as React from "react";
import { Box, Switch, Typography } from "@mui/material";
import { hasValue } from "components/installation/peak_shaving_utils";

const TEXT_PRIMARY = "#444444";
const TEXT_TERTIARY = "#888888";
const PEAK_SHAVING_BLUE = "#5B84B1";
const SOC_FILL_PRIMARY = "#FF1A3D";
const SOC_FILL_SECONDARY = "#FF475E";
const OPTIMIZATION_GREEN = "rgba(46, 125, 50, 0.16)";

function clampPercent(value) {
  return Math.max(0, Math.min(100, value));
}

function resolveMarkerTops(markers, minimumGapPx = 32) {
  const sorted = [...markers].sort((a, b) => a.lineTopPx - b.lineTopPx);
  const resolved = {};
  let previousTop = null;

  sorted.forEach((marker) => {
    const nextTop = previousTop === null ? marker.lineTopPx : Math.max(marker.lineTopPx, previousTop + minimumGapPx);
    resolved[marker.key] = nextTop;
    previousTop = nextTop;
  });

  return resolved;
}

export default function BatteryCapacityVisualization({
  minSocLimit,
  maxSocLimit,
  peakShavingMinSoc,
  peakShavingEnabled = false,
  currentSoc = null,
}) {
  const visualizationMinSoc = clampPercent(minSocLimit);
  const visualizationMaxSoc = clampPercent(maxSocLimit);

  const peakShavingParsed = Number(peakShavingMinSoc);
  const hasPeakShavingLine = peakShavingEnabled && hasValue(peakShavingMinSoc) && Number.isFinite(peakShavingParsed);
  const visualizationPeakShavingMinSoc = hasPeakShavingLine ? clampPercent(peakShavingParsed) : null;

  const reserveUpperBound = hasPeakShavingLine
    ? Math.min(visualizationPeakShavingMinSoc, visualizationMaxSoc)
    : visualizationMinSoc;
  const hasReservedPeakShavingCapacity = reserveUpperBound > visualizationMinSoc;
  // Without peak shaving, the whole min-max range counts as optimization capacity
  const isOptimizationCapacityEligible = reserveUpperBound < visualizationMaxSoc;

  const [showCurrentSocFill, setShowCurrentSocFill] = React.useState(false);

  const hasCurrentSoc = hasValue(currentSoc) && Number.isFinite(Number(currentSoc));
  const visualizationCurrentSoc = hasCurrentSoc ? clampPercent(Number(currentSoc)) : null;

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle2" sx={{ display: "block", mb: 2, color: TEXT_PRIMARY, fontWeight: 600 }}>
        Battery capacity visualization
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "center",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <VerticalBatteryFigure
          minSoc={visualizationMinSoc}
          maxSoc={visualizationMaxSoc}
          currentSoc={visualizationCurrentSoc}
          showCurrentSocFill={showCurrentSocFill}
          peakShavingMinSoc={visualizationPeakShavingMinSoc}
          hasPeakShavingLine={hasPeakShavingLine}
          hasReservedPeakShavingCapacity={hasReservedPeakShavingCapacity}
          reserveUpperBound={reserveUpperBound}
          hasOptimizationCapacity={isOptimizationCapacityEligible}
        />

        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1.5, alignSelf: "flex-start", ml: { md: 8 } }}>
          {visualizationCurrentSoc !== null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LegendItem
                variant="fill"
                color={SOC_FILL_SECONDARY}
                label="Current SOC"
                value={`${visualizationCurrentSoc.toFixed(1)}%`}
              />
              {isOptimizationCapacityEligible && (
                <Switch
                  size="small"
                  checked={showCurrentSocFill}
                  onChange={(e) => setShowCurrentSocFill(e.target.checked)}
                  inputProps={{ "aria-label": "Show current SOC fill" }}
                />
              )}
            </Box>
          )}
          {isOptimizationCapacityEligible && (
            <LegendItem variant="fill" color={OPTIMIZATION_GREEN} label="Capacity used for optimization" />
          )}
          <LegendItem
            variant="fill"
            color="rgba(0, 0, 0, 0.3)"
            label="Capacity that can not be used in any circumstances"
          />
          {hasReservedPeakShavingCapacity && (
            <LegendItem variant="hatched" color={PEAK_SHAVING_BLUE} label="Capacity reserved for Peak Shaving" />
          )}
        </Box>
      </Box>
    </Box>
  );
}

function VerticalBatteryFigure({
  minSoc,
  maxSoc,
  currentSoc,
  showCurrentSocFill = false,
  peakShavingMinSoc,
  hasPeakShavingLine,
  hasReservedPeakShavingCapacity,
  reserveUpperBound,
  hasOptimizationCapacity,
}) {
  const batteryWidth = 92;
  const batteryHeight = 220;
  const batteryTop = 14;
  const labelGap = 16;
  const labelWidth = 150;
  // The peak-shaving marker lives on the left, so only reserve space there when it's shown
  const batteryLeft = hasPeakShavingLine ? labelGap + labelWidth : labelGap;

  const topPxFor = (percent) => batteryTop + ((100 - percent) / 100) * batteryHeight;

  const maxTopPx = topPxFor(maxSoc);
  const minTopPx = topPxFor(minSoc);
  const peakTopPx = hasPeakShavingLine ? topPxFor(peakShavingMinSoc) : null;

  const rightMarkerTops = resolveMarkerTops([
    { key: "max", lineTopPx: maxTopPx },
    { key: "min", lineTopPx: minTopPx },
  ]);

  return (
    <Box
      sx={{
        position: "relative",
        width: batteryLeft + batteryWidth + labelGap + labelWidth,
        height: batteryHeight + batteryTop + 16,
      }}
    >
      {/* Battery terminal cap */}
      <Box
        sx={{
          position: "absolute",
          left: batteryLeft + batteryWidth / 2 - 16,
          top: 0,
          width: 32,
          height: 10,
          borderRadius: "4px 4px 0 0",
          bgcolor: "rgba(0, 0, 0, 0.12)",
        }}
      />

      {/* Battery shell */}
      <Box
        sx={{
          position: "absolute",
          left: batteryLeft,
          top: batteryTop,
          width: batteryWidth,
          height: batteryHeight,
          border: "1.5px solid",
          borderColor: "rgba(0, 0, 0, 0.12)",
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "rgba(0, 0, 0, 0.015)",
          boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Unusable reserve at the bottom */}
        <Box
          sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: `${minSoc}%`, bgcolor: "rgba(0, 0, 0, 0.3)" }}
        />
        {/* Unusable headroom at the top */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            height: `${Math.max(0, 100 - maxSoc)}%`,
            bgcolor: "rgba(0, 0, 0, 0.3)",
          }}
        />

        {/* Optimization capacity: flat tint, no hatch, so it reads as "available" rather than "reserved" */}
        {hasOptimizationCapacity && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `${reserveUpperBound}%`,
              height: `${Math.max(0, maxSoc - reserveUpperBound)}%`,
              bgcolor: OPTIMIZATION_GREEN,
            }}
          />
        )}

        {/* Current level liquid fill; height animates so toggling the fill looks like it's draining/filling */}
        {currentSoc !== null && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `${minSoc}%`,
              height: `${showCurrentSocFill ? Math.max(0, Math.min(currentSoc, maxSoc) - minSoc) : 0}%`,
              background: `linear-gradient(180deg, ${SOC_FILL_SECONDARY} 0%, ${SOC_FILL_PRIMARY} 100%)`,
              transition: "height 0.6s ease",
            }}
          >
            {showCurrentSocFill && (
              <Box
                sx={{
                  position: "absolute",
                  top: -6,
                  left: "-20%",
                  right: "-20%",
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: SOC_FILL_SECONDARY,
                  opacity: 0.55,
                }}
              />
            )}
          </Box>
        )}

        {/* Peak shaving reserve band, hatched so it stays visible over both the grey reserve and the fill */}
        {hasReservedPeakShavingCapacity && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `${minSoc}%`,
              height: `${Math.max(0, reserveUpperBound - minSoc)}%`,
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(91, 132, 177, 0.4) 0px, rgba(91, 132, 177, 0.4) 3px, transparent 3px, transparent 8px)",
              zIndex: 2,
            }}
          />
        )}

        <SocLine yPercent={minSoc} color="rgba(0, 0, 0, 0.35)" />
        <SocLine yPercent={maxSoc} color="rgba(0, 0, 0, 0.35)" />
        {hasPeakShavingLine && <SocLine yPercent={peakShavingMinSoc} color={PEAK_SHAVING_BLUE} dashed />}
      </Box>

      <SocMarker
        markerTopPx={rightMarkerTops.max}
        left={batteryLeft + batteryWidth + labelGap}
        title="Maximum SOC"
        subtitle={`${maxSoc}%`}
        color="rgba(0, 0, 0, 0.35)"
      />
      {hasPeakShavingLine && (
        <SocMarker
          markerTopPx={peakTopPx}
          left={0}
          width={labelWidth}
          side="left"
          title="Peak Shaving Min SOC"
          subtitle={`${peakShavingMinSoc}%`}
          color={PEAK_SHAVING_BLUE}
          dashed
        />
      )}
      <SocMarker
        markerTopPx={rightMarkerTops.min}
        left={batteryLeft + batteryWidth + labelGap}
        title="Minimum SOC"
        subtitle={`${minSoc}%`}
        color="rgba(0, 0, 0, 0.35)"
      />
    </Box>
  );
}

function SocLine({ yPercent, color, dashed = false }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: `${yPercent}%`,
        borderTop: `1.5px ${dashed ? "dashed" : "solid"}`,
        borderColor: color,
        zIndex: 3,
      }}
    />
  );
}

function SocMarker({ markerTopPx, left, width, side = "right", title, subtitle, color, dashed = false }) {
  const isLeft = side === "left";
  return (
    <Box
      sx={{
        position: "absolute",
        top: `${markerTopPx}px`,
        left,
        width,
        display: "flex",
        flexDirection: isLeft ? "row-reverse" : "row",
        alignItems: "center",
        gap: 1,
        transform: "translateY(-50%)",
      }}
    >
      <Box sx={{ width: 18, flexShrink: 0, borderTop: `1.5px ${dashed ? "dashed" : "solid"}`, borderColor: color }} />
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
      <Box>
        <Typography
          sx={{
            fontSize: "0.8em",
            fontWeight: 700,
            color: TEXT_PRIMARY,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            textAlign: isLeft ? "right" : "left",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: "0.75em",
              color: TEXT_TERTIARY,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              textAlign: isLeft ? "right" : "left",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function LegendItem({ variant, color, label, value }) {
  const isFill = variant === "fill";
  const isHatched = variant === "hatched";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          flexShrink: 0,
          overflow: "hidden",
          bgcolor: isFill ? color : "transparent",
          backgroundImage: isHatched
            ? `repeating-linear-gradient(135deg, ${color} 0px, ${color} 2px, transparent 2px, transparent 4px)`
            : "none",
          border: isFill ? "none" : "1.5px " + (isHatched ? "solid" : "dashed"),
          borderColor: color,
        }}
      />
      <Box>
        <Typography sx={{ fontSize: "0.8em", fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.3 }}>
          {label}
        </Typography>
        {value && <Typography sx={{ fontSize: "0.75em", color: TEXT_TERTIARY, lineHeight: 1.3 }}>{value}</Typography>}
      </Box>
    </Box>
  );
}
